import { useEffect } from "react";

import { contrastRatio } from "../lib/brand-color";

/**
 * Stamps shadcn-style HSL triples + font families from a tenant's
 * ``brand_config`` into the document via a managed
 * ``<style id="tilt-brand-overrides">`` element. The Tailwind config +
 * index.css resolve every design token through these vars
 * (``--primary: var(--brand-primary, fallback)``) so updating the
 * style block repaints the entire app without re-rendering any
 * component.
 *
 * Why a <style> element instead of inline html.style.setProperty:
 *   per-theme brand variants (``brand_config.dark.X``) must apply
 *   only when ``<html class="dark">``. Inline styles can't be scoped
 *   to a class, but a <style> element can carry both a ``:root``
 *   block and an ``html.dark`` block - CSS specificity then handles
 *   theme switching automatically.
 *
 * Beyond colors, this also injects:
 *   - body / heading / mono font families (with per-theme variants)
 *   - external font @import via a managed <link> element
 *
 * Mirrors tilt-admin's ``applyBrand`` helper (kept the two in lockstep
 * intentionally - the brand_config schema is shared).
 */

interface BrandStylesProps {
  readonly brand: Record<string, unknown>;
}

const COLOR_VAR_MAP: Array<readonly [string, string]> = [
  ["primary_color", "--brand-primary"],
  ["primary_foreground", "--brand-primary-foreground"],
  ["background", "--brand-background"],
  ["foreground", "--brand-foreground"],
  ["accent", "--brand-accent"],
  ["accent_foreground", "--brand-accent-foreground"],
];

const DARK_COLOR_VAR_MAP: Array<readonly [string, string]> = [
  ["primary_color", "--brand-primary-dark"],
  ["primary_foreground", "--brand-primary-foreground-dark"],
  ["background", "--brand-background-dark"],
  ["foreground", "--brand-foreground-dark"],
  ["accent", "--brand-accent-dark"],
  ["accent_foreground", "--brand-accent-foreground-dark"],
];

const FONT_VAR_MAP: Array<readonly [string, string]> = [
  ["font_family", "--brand-font-family"],
  ["heading_font_family", "--brand-heading-font-family"],
  ["mono_font_family", "--brand-mono-font-family"],
];

// HSL triple shaped like ``221 83% 53%`` or ``221.2 83.2% 53.3%``.
const HSL_TRIPLE_RE = /^\d+(\.\d+)?\s+\d+(\.\d+)?%\s+\d+(\.\d+)?%$/;

// Platform surfaces used as contrast reference backgrounds.
// Light: canvas (stone-50); dark: surface-muted (stone-800 equiv).
// These mirror index.css :root / .dark blocks; update both together.
const LIGHT_CANVAS = "60 9% 98%";
const DARK_SURFACE_MUTED = "24 7% 14%";

const WCAG_AA = 4.5;
const WCAG_HARD_FAIL = 3;

/**
 * Validate a (foreground, background) color pair and decide whether to
 * inject the foreground CSS variable.
 *
 * - ratio < 3     → strip (don't inject); platform default takes over.
 * - 3 ≤ ratio < 4.5 → inject but warn; admin should have caught this.
 * - ratio ≥ 4.5   → inject silently.
 *
 * Returns true when the variable should be injected.
 */
function shouldInject(fgHsl: string, bgHsl: string, varName: string): boolean {
  const ratio = contrastRatio(fgHsl, bgHsl);
  if (ratio == null) return true; // can't compute; let it through
  if (ratio < WCAG_HARD_FAIL) {
    console.warn(
      `[BrandStyles] Stripping ${varName}: contrast ${ratio.toFixed(2)}:1 < ${WCAG_HARD_FAIL}:1 ` +
        `(fgColor hsl(${fgHsl}) on bgColor hsl(${bgHsl})). Falling back to platform default.`,
    );
    return false;
  }
  if (ratio < WCAG_AA) {
    console.warn(
      `[BrandStyles] ${varName}: contrast ${ratio.toFixed(2)}:1 is below WCAG AA ${WCAG_AA}:1 ` +
        `(fgColor hsl(${fgHsl}) on bgColor hsl(${bgHsl})). Injecting, but accessibility may be impaired.`,
    );
  }
  return true;
}

const STYLE_ELEMENT_ID = "tilt-brand-overrides";
const FONT_LINK_ELEMENT_ID = "tilt-brand-font-import";

function buildRuleBlock(
  selector: string,
  brand: Record<string, unknown>,
  colorMap: typeof COLOR_VAR_MAP = COLOR_VAR_MAP,
  surfaceBg: string = LIGHT_CANVAS,
): string {
  const lines: string[] = [];

  // Resolve the primary and foreground values first so the pair check
  // can use them together, regardless of map iteration order.
  const primaryKey = colorMap === DARK_COLOR_VAR_MAP ? "primary_color" : "primary_color";
  const fgKey = colorMap === DARK_COLOR_VAR_MAP ? "primary_foreground" : "primary_foreground";

  const rawPrimary = brand[primaryKey];
  const rawFg = brand[fgKey];
  const primaryHsl =
    typeof rawPrimary === "string" && HSL_TRIPLE_RE.test(rawPrimary.trim())
      ? rawPrimary.trim()
      : null;
  const fgHsl = typeof rawFg === "string" && HSL_TRIPLE_RE.test(rawFg.trim()) ? rawFg.trim() : null;

  // Whether each color passed its relevant contrast check.
  const primaryOk = primaryHsl ? shouldInject(primaryHsl, surfaceBg, primaryKey) : true;
  // Button pair: foreground text on primary background.
  const fgOk =
    primaryOk && primaryHsl && fgHsl ? shouldInject(fgHsl, primaryHsl, fgKey) : fgHsl != null;

  for (const [key, cssVar] of colorMap) {
    const value = brand[key];
    if (typeof value !== "string" || !HSL_TRIPLE_RE.test(value.trim())) continue;
    // Skip primary or its foreground when they failed the contrast check.
    if (key === primaryKey && !primaryOk) continue;
    if (key === fgKey && !fgOk) continue;
    lines.push(`  ${cssVar}: ${value.trim()};`);
  }

  for (const [key, cssVar] of FONT_VAR_MAP) {
    const value = brand[key];
    if (typeof value === "string" && value.trim().length > 0) {
      lines.push(`  ${cssVar}: ${value.trim()};`);
    }
  }
  if (lines.length === 0) return "";
  return `${selector} {\n${lines.join("\n")}\n}\n`;
}

function applyFontImport(url: unknown): void {
  let linkEl = document.getElementById(FONT_LINK_ELEMENT_ID) as HTMLLinkElement | null;
  const trimmed = typeof url === "string" ? url.trim() : "";
  // Only allow https - blocks ``javascript:`` / ``data:`` payloads that
  // would let a malicious brand_config inject script content.
  if (!trimmed || !/^https:\/\//i.test(trimmed)) {
    if (linkEl) linkEl.remove();
    return;
  }
  if (!linkEl) {
    linkEl = document.createElement("link");
    linkEl.id = FONT_LINK_ELEMENT_ID;
    linkEl.rel = "stylesheet";
    document.head.appendChild(linkEl);
  }
  if (linkEl.href !== trimmed) {
    linkEl.href = trimmed;
  }
}

export function BrandStyles({ brand }: BrandStylesProps) {
  useEffect(() => {
    let styleEl = document.getElementById(STYLE_ELEMENT_ID) as HTMLStyleElement | null;
    if (!styleEl) {
      styleEl = document.createElement("style");
      styleEl.id = STYLE_ELEMENT_ID;
      document.head.appendChild(styleEl);
    }
    const lightBlock = buildRuleBlock(":root", brand, COLOR_VAR_MAP, LIGHT_CANVAS);
    const darkBrand =
      brand["dark"] && typeof brand["dark"] === "object"
        ? (brand["dark"] as Record<string, unknown>)
        : {};
    const darkBlock = buildRuleBlock(
      "html.dark",
      darkBrand,
      DARK_COLOR_VAR_MAP,
      DARK_SURFACE_MUTED,
    );
    styleEl.textContent = lightBlock + darkBlock;
    applyFontImport(brand["font_import_url"]);
    return () => {
      if (styleEl) styleEl.textContent = "";
      // Leave the font <link> in place - leaving it cached avoids a
      // network fetch on every nav. It'll be replaced on the next
      // BrandStyles mount with a different URL.
    };
  }, [brand]);
  return null;
}
